import { useGesture } from '@use-gesture/react';

interface PinchPanProps {
  scale: number;
  x: number;
  y: number;
  onChange: (updates: { scale?: number; x?: number; y?: number }) => void;
  minScale?: number;
  maxScale?: number;
}

/**
 * usePinchPan hook provides gesture handling for zooming and panning.
 * 
 * @param props - scale, x, y, and onChange callback.
 * @returns Bind function for use-gesture.
 */
export function usePinchPan({
  scale,
  x,
  y,
  onChange,
  minScale = 0.5,
  maxScale = 5,
}: PinchPanProps) {
  return useGesture(
    {
      onDrag: ({ offset: [ox, oy] }) => {
        onChange({ x: ox, y: oy });
      },
      onPinch: ({ offset: [s], delta: [ds], event }) => {
        event.preventDefault();
        event.stopPropagation();
        // Apply a multiplier to make pinch more sensitive.
        const sensitivity = 0.01;
        const newScale = scale + ds * sensitivity;
        const clampedScale = Math.max(minScale, Math.min(maxScale, newScale));
        onChange({ scale: clampedScale });
      },
    },
    {
      drag: {
        from: () => [x, y],
        // Capture pointer events to keep dragging even if the mouse leaves the element.
        pointer: { capture: true },
      },
      pinch: {
        from: () => [scale, 0],
        eventOptions: { passive: false },
        scaleBounds: { min: minScale, max: maxScale },
        rubberband: true,
      },
    }
  );
}

